import { useState } from 'react';
import type { ClubTheme } from './themes';
import { useComments, useAddComment } from './queries';

interface CommentThreadProps {
  clubId: string;
  postId: string;
  theme: ClubTheme;
}

export default function CommentThread({ clubId, postId, theme }: CommentThreadProps) {
  const [draft, setDraft] = useState('');

  const { data: comments = [], isLoading: loading } = useComments(clubId, postId);
  const addComment = useAddComment(clubId, postId);

  const handleSend = () => {
    const text = draft.trim();
    if (!text || addComment.isPending) return;
    setDraft('');
    addComment.mutate(text, {
      onError: () => setDraft(text),
    });
  };

  return (
    <div className="space-y-3">
      {loading && (
        <p className="text-xs" style={{ color: theme.colors.muted }}>Loading comments...</p>
      )}

      {!loading && comments.length === 0 && (
        <p className="text-xs" style={{ color: theme.colors.muted }}>No comments yet. Be the first.</p>
      )}

      {comments.map((comment) => (
        <div key={comment.id} className="flex gap-2">
          <div
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
            style={{ backgroundColor: `${theme.colors.primary}30`, color: theme.colors.primary }}
          >
            {(comment.author?.full_name?.[0] || '?').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-semibold" style={{ color: theme.colors.text }}>
                {comment.author?.full_name || 'Unknown'}
              </span>
              <span className="text-[10px]" style={{ color: theme.colors.muted }}>
                {new Date(comment.created_at).toLocaleString()}
              </span>
            </div>
            <p className="text-xs mt-0.5 whitespace-pre-wrap" style={{ color: theme.colors.text }}>
              {comment.body}
            </p>
          </div>
        </div>
      ))}

      {/* Composer */}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder="Write a comment..."
          className="flex-1 rounded-xl px-3 py-1.5 text-xs outline-none"
          style={{
            backgroundColor: `${theme.colors.muted}15`,
            color: theme.colors.text,
            border: `1px solid ${theme.colors.muted}30`,
          }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!draft.trim() || addComment.isPending}
          className="rounded-xl px-3 py-1.5 text-xs font-semibold transition-opacity disabled:opacity-40"
          style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
        >
          {addComment.isPending ? '...' : 'Post'}
        </button>
      </div>
    </div>
  );
}
